import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
// Adjust the import path below to where your auth store actually lives.
import { useAuthStore } from '../store/authStore';
import ragService from '../services/rag.service';
import { configAPI } from '../services/api';
import MetricHint from '../components/common/MetricHint';
import ExplainMetrics from '../components/common/ExplainMetrics';

const DASHBOARD_METRIC_DEFINITIONS = [
  {
    name: 'Collections',
    definition:
      'Knowledge-base collections group related documents — one collection per topic (e.g., Pricing, Product Docs, FAQs). Each has its own embeddings and is searched independently.',
    calculation: 'COUNT(collections WHERE tenant_id)',
  },
  {
    name: 'Documents',
    definition:
      'Source files the AI uses to answer questions — PDFs, web pages, markdown, uploaded docs. Each document is split into chunks for retrieval.',
    calculation: 'COUNT(documents across all collections)',
  },
  {
    name: 'Chunks',
    definition:
      'Documents are split into ~500-token overlapping chunks before embedding. The AI retrieves the most relevant chunks per question, not whole documents. More chunks = wider searchable surface area.',
    calculation: 'SUM(chunks per document)',
    note:
      'Roughly 5-15 chunks per typical PDF page. If chunk count seems low, the document may have low text extraction (image-only PDF).',
  },
  {
    name: 'Vector DB',
    definition:
      'The database engine storing document embeddings that powers semantic search. Different providers (pgvector, Chroma) trade off speed, cost, and scale.',
    note: 'Change in Platform Config. Not a per-tenant setting.',
  },
  {
    name: 'Quick Actions',
    definition:
      'Common first-time-user actions: upload documents, add a collection, or test a search. Not a metric — a shortcut row.',
  },
  {
    name: 'Recent Collections / Documents',
    definition:
      'Last 5 items you or your team created. Click through to open the full RAG Management view.',
  },
];

const DashboardPage = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [collections, setCollections] = useState([]);
  const [recentDocuments, setRecentDocuments] = useState([]);
  const [error, setError] = useState('');
  const [vectorDbProvider, setVectorDbProvider] = useState('Loading...');

  // Role checks
  const isSuperadmin = user?.role === 'superadmin';
  const isTenantAdmin = user?.tenant_role === 'admin' || user?.tenant_role === 'owner';
  const isAdmin = isSuperadmin || isTenantAdmin;
  const canManageSetup = isSuperadmin;

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    setError('');
    
    try {
      // Load active config to get vector DB provider
      try {
        const configResponse = await configAPI.getActiveSetup();
        const configData = configResponse?.data || configResponse;
        const setupType = configData?.setupType;
        const activeConfig = setupType === 'cloudsetup' 
          ? configData?.active_config_cloud 
          : configData?.active_config_local;
        setVectorDbProvider(activeConfig?.vector_db_provider || 'Not Configured');
      } catch (configErr) {
        console.warn('⚠️ Config loading failed (non-critical):', configErr);
        setVectorDbProvider('Unknown');
      }

      // Load collections first (most important)
      const collectionsResponse = await ragService.getCollections();
      console.log('✅ Collections:', collectionsResponse);
      setCollections(collectionsResponse.collections || []);

      // Try to load stats (non-critical)
      try {
        const statsResponse = await ragService.getStats();
        console.log('✅ Stats:', statsResponse);
        setStats(statsResponse);
      } catch (statsErr) {
        console.warn('⚠️ Stats loading failed (non-critical):', statsErr);
        // Set default stats from collections
        const totalCollections = collectionsResponse.collections?.length || 0;
        const totalDocs = collectionsResponse.collections?.reduce((sum, c) => sum + (c.documents_count || 0), 0) || 0;
        const totalChunks = collectionsResponse.collections?.reduce((sum, c) => sum + (c.chunks_count || 0), 0) || 0;
        setStats({
          total_collections: totalCollections,
          total_documents: totalDocs,
          total_chunks: totalChunks,
          vector_store: vectorDbProvider
        });
      }

      // Load recent documents from first collection (non-critical)
      if (collectionsResponse.collections && collectionsResponse.collections.length > 0) {
        try {
          const docsResponse = await ragService.getDocuments(collectionsResponse.collections[0].id);
          const docs = docsResponse.documents?.slice(0, 5).map((doc, index) => ({
            id: index,
            name: doc.source,
            chunks: doc.chunks || 0,
            collection: doc.collection,
            uploadedAt: new Date().toISOString(),
          })) || [];
          setRecentDocuments(docs);
        } catch (err) {
          console.warn('⚠️ Error loading documents (non-critical):', err);
        }
      }
    } catch (err) {
      console.error('❌ Error loading dashboard data:', err);
      // Only set error if collections failed to load
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to load dashboard data';
      setError(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Platform Status</h1>
          <p className="mt-2 text-gray-600">
            Welcome back{user?.full_name ? `, ${user.full_name}` : ''}! Here's your knowledge base and system overview.
          </p>
        </div>
        <ExplainMetrics items={DASHBOARD_METRIC_DEFINITIONS} />
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
          <span className="block sm:inline">{error}</span>
          <button 
            onClick={() => setError('')}
            className="absolute top-0 bottom-0 right-0 px-4 py-3"
          >
            <span className="text-2xl">&times;</span>
          </button>
        </div>
      )}

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Collections Card */}
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-lg p-6 text-white cursor-pointer hover:shadow-xl transition-shadow"
             onClick={() => navigate('/rag/collections')}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm font-medium inline-flex items-center gap-1">
                Collections
                <MetricHint text="Knowledge-base collections group related documents (e.g., 'Pricing', 'Product Docs', 'FAQs'). Each collection has its own embeddings and is searched independently by the AI agent." className="text-blue-100 hover:text-white" />
              </p>
              <p className="text-3xl font-bold mt-2">{stats?.total_collections || 0}</p>
              <p className="text-blue-100 text-xs mt-2">Click to manage →</p>
            </div>
            <div className="text-5xl opacity-80">📚</div>
          </div>
        </div>

        {/* Documents Card */}
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow-lg p-6 text-white cursor-pointer hover:shadow-xl transition-shadow"
             onClick={() => navigate('/rag/documents')}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm font-medium inline-flex items-center gap-1">
                Documents
                <MetricHint text="Source files the AI uses to answer questions (PDFs, web pages, uploaded docs). Each document is split into chunks for retrieval." className="text-green-100 hover:text-white" />
              </p>
              <p className="text-3xl font-bold mt-2">{stats?.total_documents || 0}</p>
              <p className="text-green-100 text-xs mt-2">Click to view →</p>
            </div>
            <div className="text-5xl opacity-80">📄</div>
          </div>
        </div>

        {/* Chunks Card */}
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm font-medium inline-flex items-center gap-1">
                Total Chunks
                <MetricHint text="Documents are split into small overlapping chunks (~500 tokens each) before being embedded. The AI retrieves the most relevant chunks per question, not whole documents." className="text-purple-100 hover:text-white" />
              </p>
              <p className="text-3xl font-bold mt-2">{stats?.total_chunks || 0}</p>
              <p className="text-purple-100 text-xs mt-2">Vector embeddings</p>
            </div>
            <div className="text-5xl opacity-80">🧩</div>
          </div>
        </div>

        {/* Vector Store Card */}
        <div
          className={`bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg shadow-lg p-6 text-white ${
            canManageSetup ? 'cursor-pointer hover:shadow-xl transition-shadow' : ''
          }`}
          onClick={() => canManageSetup && navigate('/rag/search')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-sm font-medium inline-flex items-center gap-1">
                Vector Store
                <MetricHint text="The database engine that stores document embeddings and powers semantic search. Different providers (pgvector, Chroma, etc.) trade off speed, cost and scale." className="text-orange-100 hover:text-white" />
              </p>
              <p className="text-2xl font-bold mt-2">{vectorDbProvider}</p>
              <p className="text-orange-100 text-xs mt-2">Search docs →</p>
            </div>
            <div className="text-5xl opacity-80">🗄️</div>
          </div>
        </div>
      </div>

      {/* Quick Actions - setup actions are superadmin-only */}
      {canManageSetup && (
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => navigate('/rag/upload')}
            className="flex items-center space-x-3 p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-indigo-500 hover:bg-indigo-50 transition-all group"
          >
            <div className="text-3xl">⬆️</div>
            <div className="text-left">
              <p className="font-semibold text-gray-900 group-hover:text-indigo-600">Upload Document</p>
              <p className="text-sm text-gray-600">Add new files to RAG</p>
            </div>
          </button>

          <button
            onClick={() => navigate('/rag/collections')}
            className="flex items-center space-x-3 p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition-all group"
          >
            <div className="text-3xl">➕</div>
            <div className="text-left">
              <p className="font-semibold text-gray-900 group-hover:text-green-600">Create Collection</p>
              <p className="text-sm text-gray-600">New vector collection</p>
            </div>
          </button>

          <button
            onClick={() => navigate('/rag/search')}
            className="flex items-center space-x-3 p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-all group"
          >
            <div className="text-3xl">🔍</div>
            <div className="text-left">
              <p className="font-semibold text-gray-900 group-hover:text-purple-600">Search Documents</p>
              <p className="text-sm text-gray-600">Semantic search</p>
            </div>
          </button>
        </div>
      </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Collections */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Recent Collections</h2>
            {isAdmin && (
            <button
              onClick={() => navigate('/rag/collections')}
              className="text-sm text-indigo-600 hover:text-indigo-700"
            >
              View All →
            </button>
            )}
          </div>
          <div className="divide-y divide-gray-200">
            {collections.length > 0 ? (
              collections.slice(0, 5).map((collection) => (
                <div key={collection.id} className="px-6 py-4 hover:bg-gray-50 transition-colors cursor-pointer"
                     onClick={() => navigate('/rag/collections')}>
                  <div className="flex items-center space-x-3">
                    <div className="text-2xl">📦</div>
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900">
                        {collection.name || collection.collection_name}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {collection.documents_count || 0} docs • {collection.chunks_count || 0} chunks
                      </p>
                    </div>
                    <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                      Active
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="px-6 py-12 text-center">
                <div className="text-4xl mb-2">📭</div>
                <p className="text-gray-500">No collections yet</p>
                {canManageSetup && (
                <button
                  onClick={() => navigate('/rag/collections')}
                  className="mt-3 text-sm text-indigo-600 hover:text-indigo-700"
                >
                  Create your first collection →
                </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Recent Documents */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Recent Documents</h2>
            {isAdmin && (
            <button
              onClick={() => navigate('/rag/documents')}
              className="text-sm text-indigo-600 hover:text-indigo-700"
            >
              View All →
            </button>
            )}
          </div>
          <div className="divide-y divide-gray-200">
            {recentDocuments.length > 0 ? (
              recentDocuments.map((doc) => (
                <div key={doc.id} className="px-6 py-4 hover:bg-gray-50 transition-colors cursor-pointer"
                     onClick={() => navigate('/rag/documents')}>
                  <div className="flex items-center space-x-3">
                    <div className="text-2xl">📄</div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-gray-900 truncate">
                        {doc.name}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {doc.collection} • {doc.chunks} chunks
                      </p>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="px-6 py-12 text-center">
                <div className="text-4xl mb-2">📄</div>
                <p className="text-gray-500">No documents yet</p>
                {canManageSetup && (
                <button
                  onClick={() => navigate('/rag/upload')}
                  className="mt-3 text-sm text-indigo-600 hover:text-indigo-700"
                >
                  Upload your first document →
                </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* System Status */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">System Status</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center space-x-3 p-4 bg-green-50 rounded-lg">
            <div className="text-2xl">✅</div>
            <div>
              <p className="font-medium text-gray-900">Vector Database</p>
              <p className="text-sm text-green-600">Connected</p>
            </div>
          </div>
          <div className="flex items-center space-x-3 p-4 bg-green-50 rounded-lg">
            <div className="text-2xl">✅</div>
            <div>
              <p className="font-medium text-gray-900">API Server</p>
              <p className="text-sm text-green-600">Online</p>
            </div>
          </div>
          <div className="flex items-center space-x-3 p-4 bg-blue-50 rounded-lg">
            <div className="text-2xl">ℹ️</div>
            <div>
              <p className="font-medium text-gray-900">Storage Used</p>
              <p className="text-sm text-blue-600">{stats?.total_chunks || 0} vectors</p>
            </div>
          </div>
        </div>
      </div>

      {/* Getting Started Guide (only show if no collections and user is admin) */}
      {collections.length === 0 && canManageSetup && (
        <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-lg border-2 border-indigo-200 p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-3">🚀 Getting Started with RAG</h2>
          <div className="space-y-3">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold">
                1
              </div>
              <div>
                <p className="font-semibold text-gray-900">Create a Collection</p>
                <p className="text-sm text-gray-600">Collections organize your documents in the vector database</p>
                <button
                  onClick={() => navigate('/rag/collections')}
                  className="mt-2 text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                >
                  Create Collection →
                </button>
              </div>
            </div>
            <div className="flex items-start space-x-3 opacity-50">
              <div className="flex-shrink-0 w-8 h-8 bg-gray-400 text-white rounded-full flex items-center justify-center font-bold">
                2
              </div>
              <div>
                <p className="font-semibold text-gray-900">Upload Documents</p>
                <p className="text-sm text-gray-600">Add PDF, DOCX, TXT, or other files to your collection</p>
              </div>
            </div>
            <div className="flex items-start space-x-3 opacity-50">
              <div className="flex-shrink-0 w-8 h-8 bg-gray-400 text-white rounded-full flex items-center justify-center font-bold">
                3
              </div>
              <div>
                <p className="font-semibold text-gray-900">Search & Query</p>
                <p className="text-sm text-gray-600">Use semantic search to find relevant information</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardPage;
